/**
 * Device-only journal drafts (spec §8.7 P0 privacy).
 *
 * Drafts live in localStorage under 'rust-journal-drafts', keyed by local ISO
 * date (one entry per date). They keep unsaved work on THIS device only and
 * are never presented as cloud-saved: the UI labels them 'Alleen op dit
 * apparaat' until repositories.journal.save confirms the write, after which
 * the draft for that date is cleared.
 *
 * Privacy: this module only reads/writes localStorage. Nothing here logs,
 * sends or analyses journal text.
 */
import type { SyncSaveState } from '@/types'
import type { JournalFormValues } from './scaleOptions'
import { EMPTY_JOURNAL_VALUES, valuesAreEmpty } from './scaleOptions'
import { isValidISODate } from './journalDates'

export const JOURNAL_DRAFTS_STORAGE_KEY = 'rust-journal-drafts'

export type JournalDraft = JournalFormValues & {
  state: SyncSaveState
  /** ISO timestamp of the last local draft write. */
  updatedAt: string
  /** updatedAt of the cloud entry the draft is based on, when known. */
  basedOnEntryUpdatedAt: string | null
}

export type JournalDraftMap = Record<string, JournalDraft>

function storageAvailable(): boolean {
  return typeof localStorage !== 'undefined'
}

const VALID_STATES: readonly SyncSaveState[] = [
  'local_saved',
  'sync_queued',
  'syncing',
  'cloud_saved',
  'sync_failed',
]

function isScaleValue(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 5
}

function isValidDraft(value: unknown): value is JournalDraft {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    isScaleValue(candidate.mood) &&
    isScaleValue(candidate.energy) &&
    isScaleValue(candidate.stress) &&
    typeof candidate.wentWell === 'string' &&
    typeof candidate.wasDifficult === 'string' &&
    typeof candidate.rememberTomorrow === 'string' &&
    typeof candidate.freewriting === 'string' &&
    Array.isArray(candidate.tags) &&
    candidate.tags.every((tag) => typeof tag === 'string') &&
    typeof candidate.state === 'string' &&
    VALID_STATES.includes(candidate.state as SyncSaveState) &&
    typeof candidate.updatedAt === 'string' &&
    (typeof candidate.basedOnEntryUpdatedAt === 'string' ||
      candidate.basedOnEntryUpdatedAt === null)
  )
}

export function loadJournalDrafts(): JournalDraftMap {
  if (!storageAvailable()) return {}
  try {
    const raw = localStorage.getItem(JOURNAL_DRAFTS_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    const drafts: JournalDraftMap = {}
    for (const [date, value] of Object.entries(parsed)) {
      if (isValidISODate(date) && isValidDraft(value)) drafts[date] = value
    }
    return drafts
  } catch {
    // Corrupt or unreadable storage: start clean rather than crashing the page.
    return {}
  }
}

export function writeJournalDrafts(drafts: JournalDraftMap): void {
  if (!storageAvailable()) return
  try {
    localStorage.setItem(JOURNAL_DRAFTS_STORAGE_KEY, JSON.stringify(drafts))
  } catch {
    // Storage full or blocked: the in-memory form state still protects this session.
  }
}

export function getJournalDraft(date: string): JournalDraft | null {
  return loadJournalDrafts()[date] ?? null
}

export type SaveJournalDraftInput = JournalFormValues & {
  state: SyncSaveState
  basedOnEntryUpdatedAt: string | null
}

/**
 * Upserts the draft for a date. Empty drafts (nothing entered, no cloud entry)
 * are removed instead of stored, so the draft list stays honest.
 */
export function saveJournalDraft(date: string, input: SaveJournalDraftInput): JournalDraftMap {
  const drafts = loadJournalDrafts()
  if (valuesAreEmpty(input) && input.basedOnEntryUpdatedAt === null) {
    delete drafts[date]
  } else {
    drafts[date] = {
      mood: input.mood,
      energy: input.energy,
      stress: input.stress,
      wentWell: input.wentWell,
      wasDifficult: input.wasDifficult,
      rememberTomorrow: input.rememberTomorrow,
      freewriting: input.freewriting,
      tags: [...input.tags],
      state: input.state,
      basedOnEntryUpdatedAt: input.basedOnEntryUpdatedAt,
      updatedAt: new Date().toISOString(),
    }
  }
  writeJournalDrafts(drafts)
  return drafts
}

export function removeJournalDraft(date: string): JournalDraftMap {
  const drafts = loadJournalDrafts()
  delete drafts[date]
  writeJournalDrafts(drafts)
  return drafts
}

/** Test/util helper: build a valid draft object without touching storage. */
export function makeDraft(overrides: Partial<JournalDraft> = {}): JournalDraft {
  return {
    ...EMPTY_JOURNAL_VALUES,
    wentWell: 'iets',
    state: 'local_saved',
    updatedAt: new Date().toISOString(),
    basedOnEntryUpdatedAt: null,
    ...overrides,
    tags: overrides.tags ? [...overrides.tags] : [],
  }
}
